import * as React from 'react';

import { IRouter, Link } from 'react-router';
import { url, submitForm } from '../../util';

import Input from '../form/Input';
import DateInput from '../form/DateInput';
import SelectInput from '../form/SelectInput';
import BackButton from '../common/BackButton';

import { IError, IOwner, IPetRequest, IEditablePet, IPet, IPetType, IRouterContext, ISelectOption } from '../../types';

interface IPetEditorProps {
  pet: IEditablePet;
  owner: IOwner;
  pettypes: ISelectOption[];
}

interface IPetEditorState {
  editablePet?: IEditablePet;
  error?: IError;
};

export default class PetEditor extends React.Component<IPetEditorProps, IPetEditorState> {

  context: IRouterContext;
  private _isMounted: boolean = false;

  static contextTypes = {
    router: React.PropTypes.object.isRequired
  };

  constructor(props: IPetEditorProps) {
    super(props);
    this.onInputChange = this.onInputChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    // Initialize editablePet from props.pet, ensuring it's always an object
    // Convert pet from backend format (with type object) to editable format (with typeId)
    const initialPet = this.convertPetToEditable(props.pet);
    console.log('PetEditor constructor - props.pet:', props.pet, 'initialPet:', initialPet);
    this.state = { editablePet: initialPet };
  }

  componentWillReceiveProps(nextProps: IPetEditorProps) {
    // Update editablePet when pet prop changes
    // This is critical for edit mode when data loads asynchronously
    if (nextProps.pet) {
      const editablePet = this.convertPetToEditable(nextProps.pet);
      const currentEditablePet = this.state.editablePet;
      
      // Always update if:
      // 1. We don't have editablePet yet (initial load)
      // 2. Pet prop changed (new data loaded) - compare by reference or ID
      // 3. Current editablePet is empty/default (data not loaded yet)
      // 4. Pet ID changed (switching between different pets)
      // 5. Current editablePet has no name but new one does (data just loaded)
      const currentPetId = currentEditablePet && currentEditablePet.id;
      const newPetId = editablePet && editablePet.id;
      const petIdChanged = currentPetId !== newPetId;
      const nameChanged = (currentEditablePet && currentEditablePet.name === '' && editablePet.name !== '');
      
      const shouldUpdate = !currentEditablePet || 
                          !currentEditablePet.id || 
                          nextProps.pet !== this.props.pet ||
                          petIdChanged ||
                          nameChanged ||
                          (currentEditablePet.name === '' && editablePet.name !== '');
      
      if (shouldUpdate) {
        console.log('componentWillReceiveProps - Updating editablePet from props:', nextProps.pet, 'Converted to:', editablePet);
        console.log('componentWillReceiveProps - Current editablePet:', currentEditablePet);
        this.setState({
          editablePet: editablePet
        });
      } else {
        console.log('componentWillReceiveProps - Skipping update. Current:', currentEditablePet, 'New:', editablePet);
      }
    }
  }

  // Convert pet from backend format (with type object) to editable format (with typeId)
  convertPetToEditable(pet: any): IEditablePet {
    if (!pet) {
      console.log('convertPetToEditable: pet is null/undefined, returning default');
      return { id: null, isNew: true, name: '', birthDate: null, typeId: null };
    }

    console.log('Converting pet to editable format - Full pet object:', JSON.stringify(pet, null, 2));
    console.log('Converting pet to editable format - pet.name:', pet.name, 'type:', typeof pet.name);
    console.log('Converting pet to editable format - pet.birthDate:', pet.birthDate, 'type:', typeof pet.birthDate);
    console.log('Converting pet to editable format - pet.type:', pet.type, 'type:', typeof pet.type);

    // CRITICAL: Determine if this is a new pet or existing pet
    // If pet has an ID, it's an existing pet (edit mode), so isNew should be false
    const hasId = pet.id !== null && pet.id !== undefined;
    const isNew = pet.isNew !== undefined ? pet.isNew : !hasId;
    console.log('convertPetToEditable - hasId:', hasId, 'isNew:', isNew);

    // Extract name - handle various formats
    let name = '';
    if (pet.name !== undefined && pet.name !== null) {
      name = String(pet.name).trim();
      console.log('Extracted name:', name);
    } else {
      console.warn('convertPetToEditable - pet.name is undefined or null');
    }

    // Extract typeId from type object if it exists
    let typeId = null;
    if (pet.type) {
      if (pet.type.id !== undefined && pet.type.id !== null) {
        typeId = pet.type.id;
        console.log('Extracted typeId from type.id:', typeId);
      } else {
        console.warn('convertPetToEditable - pet.type exists but type.id is missing:', pet.type);
      }
    } else if (pet.typeId !== undefined && pet.typeId !== null) {
      typeId = pet.typeId;
      console.log('Using existing typeId:', typeId);
    } else {
      console.warn('convertPetToEditable - No type or typeId found in pet object');
    }

    // Ensure birthDate is in string format (YYYY-MM-DD)
    let birthDate = null;
    if (pet.birthDate !== undefined && pet.birthDate !== null) {
      // If it's already a string, use it; otherwise convert Date to string
      if (typeof pet.birthDate === 'string') {
        // If it's already in YYYY-MM-DD format, use it directly
        // Handle cases where it might be a full ISO string
        if (pet.birthDate.includes('T')) {
          birthDate = pet.birthDate.split('T')[0];
        } else {
          birthDate = pet.birthDate;
        }
        console.log('Using birthDate as string:', birthDate);
      } else if (pet.birthDate instanceof Date) {
        birthDate = pet.birthDate.toISOString().split('T')[0];
        console.log('Converted Date to string:', birthDate);
      } else {
        // Try to parse it as a date string
        try {
          const date = new Date(pet.birthDate);
          if (!isNaN(date.getTime())) {
            birthDate = date.toISOString().split('T')[0];
            console.log('Parsed birthDate:', pet.birthDate, 'to:', birthDate);
          } else {
            console.warn('Invalid date value:', pet.birthDate);
          }
        } catch (e) {
          console.warn('Could not parse birthDate:', pet.birthDate, 'Error:', e);
        }
      }
    } else {
      console.warn('convertPetToEditable - pet.birthDate is undefined or null');
    }

    const result = {
      id: pet.id !== undefined && pet.id !== null ? pet.id : null,
      isNew: isNew, // Explicitly set based on whether pet has an ID
      name: name,
      birthDate: birthDate,
      typeId: typeId
    };

    console.log('Converted pet result:', result);
    console.log('Converted pet result - name:', result.name, 'birthDate:', result.birthDate, 'typeId:', result.typeId);
    return result;
  }

  componentDidMount() {
    this._isMounted = true;
    // If pet prop is available after mount, ensure we convert it
    // This is critical for edit mode when data loads asynchronously
    if (this.props.pet) {
      const editablePet = this.convertPetToEditable(this.props.pet);
      const currentEditablePet = this.state.editablePet;
      
      // Always update if we don't have editablePet, if it has no ID, if name is empty, or if IDs don't match
      const shouldUpdate = !currentEditablePet || 
                          !currentEditablePet.id || 
                          currentEditablePet.name === '' ||
                          (editablePet.id && currentEditablePet.id !== editablePet.id) ||
                          (editablePet.id && editablePet.name && currentEditablePet.name === '');
      
      if (shouldUpdate) {
        console.log('componentDidMount - Converting pet:', this.props.pet, 'to:', editablePet);
        console.log('componentDidMount - Current editablePet:', currentEditablePet);
        this.setState({ editablePet: editablePet });
      } else {
        console.log('componentDidMount - Skipping update. Current:', currentEditablePet, 'New:', editablePet);
      }
    } else {
      console.log('componentDidMount - No pet prop available');
    }
  }

  componentDidUpdate(prevProps: IPetEditorProps) {
    // Update if pet prop changed and we have new data
    // This is a fallback in case componentWillReceiveProps didn't trigger
    if (this.props.pet && this.props.pet !== prevProps.pet) {
      const editablePet = this.convertPetToEditable(this.props.pet);
      const currentEditablePet = this.state.editablePet;
      
      // Update if we don't have data yet, if the pet ID changed, or if name is empty but should have data
      const shouldUpdate = !currentEditablePet || 
                          !currentEditablePet.id || 
                          (editablePet.id && currentEditablePet.id !== editablePet.id) ||
                          (currentEditablePet.name === '' && editablePet.name !== '');
      
      if (shouldUpdate) {
        console.log('componentDidUpdate - Updating editablePet:', editablePet, 'Current:', currentEditablePet);
        this.setState({ editablePet: editablePet });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const { owner, pettypes } = this.props;
    const { editablePet } = this.state;

    // Ensure editablePet exists
    if (!editablePet || !owner || !pettypes) {
      console.error('Cannot submit: editablePet, owner, or pettypes is missing', { editablePet, owner, pettypes });
      return;
    }

    // Find the pet type object from pettypes
    // Note: ISelectOption uses 'value' for the ID, not 'id'
    // Handle type coercion: typeId might be string or number, value might be string or number
    const selectedPetType = pettypes.find(pt => {
      // Convert both to numbers for comparison to handle string/number mismatch
      const ptValue = typeof pt.value === 'string' ? parseInt(pt.value, 10) : pt.value;
      const typeId = typeof editablePet.typeId === 'string' ? parseInt(editablePet.typeId, 10) : editablePet.typeId;
      return ptValue === typeId;
    });
    if (!selectedPetType) {
      console.error('Selected pet type not found:', editablePet.typeId, 'Type:', typeof editablePet.typeId, 'Available pettypes:', pettypes);
      if (this._isMounted) {
        this.setState({ 
          error: { 
            fieldErrors: {}, 
            message: 'Please select a valid pet type' 
          } 
        });
      }
      return;
    }

    // Ensure all required fields are present
    // Backend expects PetFieldsDto with: name, birthDate, and type (object with id and name)
    const request: any = {
      name: editablePet.name || '',
      birthDate: editablePet.birthDate || null,
      type: {
        id: selectedPetType.value, // ISelectOption uses 'value' for the ID
        name: selectedPetType.name || ''
      }
    };

    // Validate required fields
    if (!request.name || !request.type || !request.type.id) {
      console.error('Missing required fields:', request);
      if (this._isMounted) {
        this.setState({ 
          error: { 
            fieldErrors: {}, 
            message: 'Name and Type are required fields' 
          } 
        });
      }
      return;
    }

    const url = editablePet.isNew ? '/api/owners/' + owner.id + '/pets' :  '/api/owners/' + owner.id + '/pets/' + editablePet.id;
    submitForm(editablePet.isNew ? 'POST' : 'PUT', url, request, (status, response) => {
      if (!this._isMounted) {
        return;
      }

      if (status === 200 || status === 201 || status === 204) {
        console.log('Pet saved successfully. Status:', status);
        // Use window.location to avoid React update conflicts
        setTimeout(() => {
          window.location.href = `/owners/${owner.id}`;
        }, 100);
      } else {
        console.error('Error saving pet. Status:', status, 'Response:', response);
        if (this._isMounted) {
          // CRITICAL: Preserve editablePet when setting error state
          // This prevents editablePet from becoming undefined during error handling
          const currentEditablePet = this.state.editablePet;
          if (!currentEditablePet || typeof currentEditablePet !== 'object') {
            // If editablePet is invalid, restore it from props or create a default
            const fallbackPet = this.props.pet || { 
              id: null, 
              isNew: true, 
              name: '', 
              birthDate: null, 
              typeId: null 
            };
            this.setState({ 
              error: response,
              editablePet: Object.assign({}, fallbackPet)
            });
          } else {
            // editablePet is valid, just set the error
            this.setState({ error: response });
          }
        }
      }
    });
  }

  onInputChange(name: string, value: string) {
    const { editablePet } = this.state;
    
    // Ensure editablePet exists before modifying
    if (!editablePet || typeof editablePet !== 'object') {
      console.error('onInputChange: editablePet is invalid', editablePet);
      return;
    }
    
    const modifiedPet = Object.assign({}, editablePet, { [name]: value });
    console.log('onInputChange:', name, '=', value, 'modifiedPet:', modifiedPet);

    this.setState({ editablePet: modifiedPet });
  }

  render() {
    const { owner, pettypes } = this.props;
    const { editablePet, error } = this.state;

    // Don't render form until owner and editablePet are initialized
    // Double-check that editablePet is a valid object (not just truthy)
    if (!owner || !editablePet || typeof editablePet !== 'object' || !pettypes) {
      return <div>Loading...</div>;
    }

    // Create a safe copy with all required properties
    // Double-check editablePet is still valid before accessing properties
    if (!editablePet || typeof editablePet !== 'object') {
      console.error('PetEditor: editablePet is invalid in render', editablePet);
      return <div>Error: Invalid pet data. Please refresh the page.</div>;
    }
    
    // Create safe copy with all required properties
    // CRITICAL: Ensure isNew is correctly set based on whether pet has an ID
    const hasId = editablePet.id !== null && editablePet.id !== undefined;
    const isNew = editablePet.isNew !== undefined ? editablePet.isNew : !hasId;
    
    // Convert typeId to string for SelectInput compatibility (HTML select values are strings)
    const safeEditablePet = {
      id: editablePet.id !== undefined && editablePet.id !== null ? editablePet.id : null,
      isNew: isNew, // Explicitly set based on whether pet has an ID
      name: editablePet.name !== undefined ? editablePet.name : '',
      birthDate: editablePet.birthDate !== undefined ? editablePet.birthDate : null,
      // Convert typeId to string for SelectInput (HTML select elements use string values)
      typeId: editablePet.typeId !== undefined && editablePet.typeId !== null 
        ? String(editablePet.typeId) 
        : null
    };
    
    console.log('Render - editablePet:', editablePet, 'safeEditablePet:', safeEditablePet);
    console.log('Render - safeEditablePet.name:', safeEditablePet.name, 'safeEditablePet.birthDate:', safeEditablePet.birthDate, 'safeEditablePet.typeId:', safeEditablePet.typeId);
    
    // Final validation - ensure safeEditablePet is a valid object
    if (!safeEditablePet || typeof safeEditablePet !== 'object') {
      console.error('PetEditor: safeEditablePet creation failed', safeEditablePet);
      return <div>Error: Failed to create pet data. Please refresh the page.</div>;
    }

    const formLabel = safeEditablePet.isNew ? 'Add Pet' : 'Edit Pet';

    return (
      <span>
        <BackButton />
        <h2>{formLabel}</h2>
        <form className='form-horizontal' method='POST' action={url('/api/owner')}>
          <div className='form-group has-feedback'>
            <div className='form-group'>
              <label className='col-sm-2 control-label'>Owner</label>
              <div className='col-sm-10'>{owner.firstName} {owner.lastName}</div>
            </div>

            <Input object={safeEditablePet} error={error} label='Name' name='name' onChange={this.onInputChange} />
            {safeEditablePet && typeof safeEditablePet === 'object' && !Array.isArray(safeEditablePet) ? (
              <DateInput object={safeEditablePet} error={error} label='Birth date' name='birthDate' onChange={this.onInputChange} />
            ) : null}
            <SelectInput object={safeEditablePet} error={error} label='Type' name='typeId' options={pettypes} onChange={this.onInputChange} />
          </div>
          <div className='form-group'>
            <div className='col-sm-offset-2 col-sm-10'>
              <button className='btn btn-default' type='button' onClick={this.onSubmit}>{formLabel}</button>
            </div>
          </div>
        </form>
      </span>
    );
  }
}
